import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CalendarClock,
  Check,
  Coins,
  Globe,
  Mail,
  Megaphone,
  MessageCircle,
  MessageSquare,
  Smartphone,
  Users,
  X,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import {
  CampaignsApi,
  campaignKeys,
  type ApiCampaign,
} from "@/api/perkpoint/campaigns";
import { apiErrorMessage } from "@/api/perkpoint/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { campaignRowFromApi, type CampaignRow, type CampaignStatus } from "./campaign-types";

type CampaignDetailsSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  programId: string | null;
  campaignId: string | null;
  /** List-row fallback while detail loads */
  summary?: CampaignRow | null;
};

function dash(value: unknown): string {
  if (value == null || value === "") return "—";
  return String(value);
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const STATUS_STYLE: Record<
  CampaignStatus,
  { wrap: string; dot: string }
> = {
  Ongoing: { wrap: "bg-green-50 text-green-800 ring-green-200", dot: "bg-green-500" },
  Scheduled: { wrap: "bg-sky-50 text-sky-800 ring-sky-200", dot: "bg-sky-500" },
  "Pending approval": {
    wrap: "bg-amber-50 text-amber-900 ring-amber-200",
    dot: "bg-amber-500",
  },
  Ended: { wrap: "bg-slate-100 text-slate-700 ring-slate-200", dot: "bg-slate-400" },
  Draft: { wrap: "bg-slate-100 text-slate-700 ring-slate-200", dot: "bg-slate-500" },
};

function StatusPill({ status }: { status: CampaignStatus }) {
  const style = STATUS_STYLE[status];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset",
        style.wrap,
      )}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", style.dot)} />
      {status}
    </span>
  );
}

function SoftChip({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-800 ring-1 ring-inset ring-emerald-100">
      {children}
    </span>
  );
}

function DetailGrid({
  items,
}: {
  items: { label: string; value: ReactNode }[];
}) {
  return (
    <dl className="grid gap-3 sm:grid-cols-2">
      {items.map((item) => (
        <div
          key={item.label}
          className="min-w-0 rounded-lg bg-slate-50/80 px-3 py-2.5"
        >
          <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            {item.label}
          </dt>
          <dd className="mt-1 break-words text-sm font-medium text-slate-900">
            {item.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function SectionCard({
  title,
  icon: Icon,
  children,
  className,
  accent = false,
}: {
  title?: string;
  icon?: typeof Megaphone;
  children: ReactNode;
  className?: string;
  accent?: boolean;
}) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm",
        accent && "border-green-200",
        className,
      )}
    >
      {title ? (
        <div
          className={cn(
            "flex items-center gap-2 border-b px-4 py-3 sm:px-5",
            accent
              ? "border-green-100 bg-gradient-to-r from-green-50 to-emerald-50/40"
              : "border-slate-100 bg-slate-50/60",
          )}
        >
          {Icon ? (
            <span
              className={cn(
                "inline-flex h-7 w-7 items-center justify-center rounded-lg",
                accent
                  ? "bg-green-600 text-white"
                  : "bg-white text-green-700 ring-1 ring-slate-200",
              )}
            >
              <Icon className="h-3.5 w-3.5" />
            </span>
          ) : null}
          <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        </div>
      ) : null}
      <div className="p-4 sm:p-5">{children}</div>
    </div>
  );
}

function channelLabel(channel: string) {
  const map: Record<string, string> = {
    whatsapp: "WhatsApp",
    email: "Email",
    sms: "SMS",
    in_app: "In-app",
    inapp: "In-app",
    web: "Web",
  };
  return map[channel.toLowerCase()] ?? channel;
}

function ChannelIcon({ channel }: { channel: string }) {
  const key = channel.toLowerCase();
  const Icon =
    key === "whatsapp"
      ? MessageCircle
      : key === "email"
        ? Mail
        : key === "sms"
          ? MessageSquare
          : key === "in_app" || key === "inapp"
            ? Smartphone
            : Globe;
  return <Icon className="h-4 w-4" />;
}

function StatTile({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string;
  icon: typeof Coins;
}) {
  return (
    <div className="rounded-xl border border-green-100 bg-white/80 px-3 py-2.5 shadow-sm backdrop-blur">
      <div className="flex items-center gap-2 text-green-700">
        <Icon className="h-3.5 w-3.5" />
        <span className="text-[11px] font-semibold uppercase tracking-wide">
          {label}
        </span>
      </div>
      <p className="mt-1 truncate text-sm font-semibold text-slate-900">{value}</p>
    </div>
  );
}

function OverviewTab({
  detail,
  row,
}: {
  detail: ApiCampaign;
  row: CampaignRow;
}) {
  return (
    <div className="space-y-4">
      <SectionCard title="Basics" icon={Megaphone} accent>
        <DetailGrid
          items={[
            { label: "Code", value: row.code },
            { label: "Status", value: <StatusPill status={row.status} /> },
            { label: "Type", value: row.type },
            { label: "Objective", value: dash(detail.objective) },
            { label: "Name", value: detail.name },
            {
              label: "Description",
              value: detail.description?.trim() || "—",
            },
            {
              label: "Requires approval",
              value: detail.requires_approval ? "Yes" : "No",
            },
            {
              label: "Created",
              value: formatDateTime(detail.created_at as string | undefined),
            },
            {
              label: "Updated",
              value: formatDateTime(detail.updated_at as string | undefined),
            },
          ]}
        />
      </SectionCard>
      <SectionCard title="Lifecycle" icon={Zap}>
        <DetailGrid
          items={[
            {
              label: "Submitted",
              value: formatDateTime(detail.submitted_at as string | undefined),
            },
            {
              label: "Approved",
              value: formatDateTime(detail.approved_at as string | undefined),
            },
            {
              label: "Rejected",
              value: formatDateTime(detail.rejected_at as string | undefined),
            },
            {
              label: "Rejection reason",
              value: dash(detail.rejection_reason),
            },
            {
              label: "Completed",
              value: formatDateTime(detail.completed_at as string | undefined),
            },
            {
              label: "Next run",
              value: formatDateTime(detail.next_run_at as string | undefined),
            },
          ]}
        />
      </SectionCard>
    </div>
  );
}

function AudienceTab({ detail }: { detail: ApiCampaign }) {
  const audience = detail.audience as
    | {
        all_members?: boolean;
        control_group_pct?: number | string;
        segment_ids?: string[];
        segments?: { id?: string; key?: string; name?: string; label?: string }[];
        include_tiers?: { id?: string; key?: string; name?: string }[];
        exclude_tiers?: { id?: string; key?: string; name?: string }[];
        include_tier_ids?: string[];
        exclude_tier_ids?: string[];
      }
    | null
    | undefined;

  if (!audience) {
    return (
      <SectionCard title="Audience" icon={Users}>
        <p className="text-sm text-muted-foreground">No audience configured.</p>
      </SectionCard>
    );
  }

  const segments =
    audience.segments
      ?.map((s) => s.name || s.label || s.key || s.id)
      .filter(Boolean) ?? [];
  const includeTiers =
    audience.include_tiers
      ?.map((t) => t.name || t.key || t.id)
      .filter(Boolean) ?? [];
  const excludeTiers =
    audience.exclude_tiers
      ?.map((t) => t.name || t.key || t.id)
      .filter(Boolean) ?? [];

  return (
    <div className="space-y-4">
      <SectionCard title="Who receives it" icon={Users} accent>
        <div className="mb-4 grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl bg-gradient-to-br from-green-50 to-emerald-50/50 p-4 ring-1 ring-green-100">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-green-800/70">
              Reach
            </p>
            <p className="mt-1 text-lg font-bold text-slate-900">
              {audience.all_members ? "All members" : "Targeted segments"}
            </p>
          </div>
          <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Control group
            </p>
            <p className="mt-1 text-lg font-bold text-slate-900">
              {audience.control_group_pct != null
                ? `${Number(audience.control_group_pct)}%`
                : "—"}
            </p>
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Segments
            </p>
            <div className="flex flex-wrap gap-1.5">
              {segments.length ? (
                segments.map((s) => <SoftChip key={String(s)}>{s}</SoftChip>)
              ) : audience.all_members ? (
                <SoftChip>All members</SoftChip>
              ) : (
                <span className="text-sm text-muted-foreground">—</span>
              )}
            </div>
          </div>
          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Include tiers
            </p>
            <div className="flex flex-wrap gap-1.5">
              {includeTiers.length ? (
                includeTiers.map((t) => <SoftChip key={String(t)}>{t}</SoftChip>)
              ) : audience.include_tier_ids?.length ? (
                audience.include_tier_ids.map((id) => (
                  <SoftChip key={id}>{id}</SoftChip>
                ))
              ) : (
                <span className="text-sm text-muted-foreground">—</span>
              )}
            </div>
          </div>
          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Exclude tiers
            </p>
            <div className="flex flex-wrap gap-1.5">
              {excludeTiers.length ? (
                excludeTiers.map((t) => (
                  <span
                    key={String(t)}
                    className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700 ring-1 ring-inset ring-slate-200"
                  >
                    {t}
                  </span>
                ))
              ) : audience.exclude_tier_ids?.length ? (
                audience.exclude_tier_ids.map((id) => (
                  <span
                    key={id}
                    className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700 ring-1 ring-inset ring-slate-200"
                  >
                    {id}
                  </span>
                ))
              ) : (
                <span className="text-sm text-muted-foreground">—</span>
              )}
            </div>
          </div>
        </div>
      </SectionCard>
    </div>
  );
}

function RewardTab({ detail }: { detail: ApiCampaign }) {
  const reward = detail.reward;
  if (!reward || reward.enabled === false) {
    return (
      <SectionCard title="Reward" icon={Coins}>
        <p className="mb-4 text-sm text-muted-foreground">
          No points offer on this campaign.
        </p>
        <DetailGrid
          items={[
            {
              label: "Goal event",
              value: dash(detail.goal_event_type_key),
            },
            {
              label: "Attribution window",
              value:
                detail.attribution_window_days != null
                  ? `${detail.attribution_window_days} days`
                  : "—",
            },
            {
              label: "Budget points",
              value: dash(detail.budget_points),
            },
          ]}
        />
      </SectionCard>
    );
  }

  const headline =
    reward.type === "multiply" && reward.multiplier != null
      ? `${reward.multiplier}x points`
      : reward.type === "fixed" && reward.points != null
        ? `${Number(reward.points).toLocaleString("en-IN")} pts`
        : "Points offer";

  return (
    <SectionCard title="Points offer" icon={Coins} accent>
      <div className="mb-4 rounded-xl bg-gradient-to-br from-green-600 to-emerald-700 p-4 text-white shadow-sm">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-green-100">
          Offer
        </p>
        <p className="mt-1 text-2xl font-bold tracking-tight">{headline}</p>
        {reward.when ? (
          <p className="mt-1 text-sm text-green-100">When · {reward.when}</p>
        ) : null}
      </div>
      <DetailGrid
        items={[
          { label: "Type", value: dash(reward.type) },
          {
            label: "Cap per member",
            value:
              reward.cap_per_member != null
                ? Number(reward.cap_per_member).toLocaleString("en-IN")
                : "—",
          },
          {
            label: "Total budget",
            value:
              reward.total_budget != null
                ? Number(reward.total_budget).toLocaleString("en-IN")
                : dash(detail.budget_points),
          },
          {
            label: "Goal event",
            value: dash(detail.goal_event_type_key),
          },
          {
            label: "Attribution window",
            value:
              detail.attribution_window_days != null
                ? `${detail.attribution_window_days} days`
                : "—",
          },
        ]}
      />
    </SectionCard>
  );
}

function ChannelsTab({ detail }: { detail: ApiCampaign }) {
  const channels = detail.channels ?? [];
  if (!channels.length) {
    return (
      <SectionCard title="Channels" icon={MessageCircle}>
        <p className="text-sm text-muted-foreground">No channels configured.</p>
      </SectionCard>
    );
  }

  return (
    <div className="space-y-4">
      {channels.map((item, index) => {
        if (typeof item === "string") {
          return (
            <SectionCard
              key={`${item}-${index}`}
              title={channelLabel(item)}
              icon={MessageCircle}
            >
              <p className="text-sm text-muted-foreground">Channel selected.</p>
            </SectionCard>
          );
        }
        const ch = item as {
          channel?: string;
          template_name?: string | null;
          subject?: string | null;
          title?: string | null;
          header?: string | null;
          body?: string | null;
          html_body?: string | null;
          footer?: string | null;
          button_label?: string | null;
          button_link?: string | null;
          image_url?: string | null;
          variables?: string[];
        };
        const label = channelLabel(ch.channel || `Channel ${index + 1}`);
        return (
          <div
            key={`${ch.channel}-${index}`}
            className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
          >
            <div className="flex items-center gap-3 border-b border-green-100 bg-gradient-to-r from-green-50 to-white px-4 py-3 sm:px-5">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-green-600 text-white shadow-sm">
                <ChannelIcon channel={ch.channel || ""} />
              </span>
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-slate-900">{label}</h3>
                {ch.template_name || ch.subject ? (
                  <p className="truncate text-xs text-muted-foreground">
                    {ch.template_name || ch.subject}
                  </p>
                ) : null}
              </div>
            </div>
            <div className="p-4 sm:p-5">
              {ch.body ? (
                <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50/80 p-3">
                  <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Message preview
                  </p>
                  <p className="whitespace-pre-wrap text-sm text-slate-800">
                    {ch.body}
                  </p>
                </div>
              ) : null}
              <DetailGrid
                items={[
                  { label: "Template", value: dash(ch.template_name) },
                  { label: "Subject", value: dash(ch.subject) },
                  { label: "Title", value: dash(ch.title) },
                  { label: "Header", value: dash(ch.header) },
                  { label: "Footer", value: dash(ch.footer) },
                  { label: "Button label", value: dash(ch.button_label) },
                  { label: "Button link", value: dash(ch.button_link) },
                  { label: "Image URL", value: dash(ch.image_url) },
                  {
                    label: "Variables",
                    value: ch.variables?.length ? ch.variables.join(", ") : "—",
                  },
                ]}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ScheduleTab({ detail }: { detail: ApiCampaign }) {
  const schedule = detail.schedule as
    | {
        mode?: string;
        send_at?: string;
        ends_at?: string;
        time_zone?: string;
        respect_quiet_hours?: boolean;
        max_messages_per_day?: number;
        event_type_key?: string;
        delay_minutes?: number;
        repeat?: {
          frequency?: string;
          time?: string;
          day_of_week?: number;
          day_of_month?: number;
        };
      }
    | null
    | undefined;

  const quiet = detail.quiet_hours_override as
    | { enabled?: boolean; start?: string; end?: string }
    | null
    | undefined;
  const freq = detail.freq_cap_override as
    | { days?: number; messages?: number }
    | null
    | undefined;

  return (
    <div className="space-y-4">
      <SectionCard title="Schedule" icon={CalendarClock} accent>
        <DetailGrid
          items={[
            { label: "Mode", value: dash(schedule?.mode || detail.trigger_type) },
            {
              label: "Send at",
              value: formatDateTime(schedule?.send_at || detail.scheduled_at),
            },
            {
              label: "Starts",
              value: formatDateTime(detail.starts_at),
            },
            {
              label: "Ends",
              value: formatDateTime(schedule?.ends_at || detail.ends_at),
            },
            {
              label: "Time zone",
              value: dash(schedule?.time_zone || detail.time_zone),
            },
            {
              label: "Event",
              value: dash(
                schedule?.event_type_key || detail.trigger_event_type_key,
              ),
            },
            {
              label: "Delay",
              value:
                schedule?.delay_minutes != null
                  ? `${schedule.delay_minutes} minutes`
                  : detail.delay_seconds != null
                    ? `${detail.delay_seconds} seconds`
                    : "—",
            },
            {
              label: "Repeat",
              value: schedule?.repeat
                ? [
                    schedule.repeat.frequency,
                    schedule.repeat.time,
                    schedule.repeat.day_of_week != null
                      ? `day ${schedule.repeat.day_of_week}`
                      : null,
                    schedule.repeat.day_of_month != null
                      ? `date ${schedule.repeat.day_of_month}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")
                : dash(detail.recurrence_cron),
            },
            {
              label: "Respect quiet hours",
              value:
                schedule?.respect_quiet_hours == null
                  ? "—"
                  : schedule.respect_quiet_hours
                    ? "Yes"
                    : "No",
            },
            {
              label: "Max messages / day",
              value: dash(schedule?.max_messages_per_day),
            },
          ]}
        />
      </SectionCard>
      <SectionCard title="Guardrails" icon={Zap}>
        <DetailGrid
          items={[
            {
              label: "Quiet hours",
              value: quiet?.enabled
                ? `${quiet.start || "—"} – ${quiet.end || "—"}`
                : quiet
                  ? "Off"
                  : "—",
            },
            {
              label: "Frequency cap",
              value:
                freq?.messages != null
                  ? `${freq.messages} / ${freq.days ?? 1} day(s)`
                  : "—",
            },
          ]}
        />
      </SectionCard>
    </div>
  );
}

export function CampaignDetailsSheet({
  open,
  onOpenChange,
  programId,
  campaignId,
  summary,
}: CampaignDetailsSheetProps) {
  const queryClient = useQueryClient();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => {
    if (!open) {
      setRejectOpen(false);
      setRejectReason("");
    }
  }, [open, campaignId]);

  const detailQuery = useQuery({
    queryKey: campaignKeys.detail(programId ?? "", campaignId ?? ""),
    queryFn: () => CampaignsApi.get(programId!, campaignId!),
    enabled: open && Boolean(programId && campaignId),
  });

  const detail = detailQuery.data;
  const row = detail ? campaignRowFromApi(detail) : summary ?? null;
  const channelCount = detail?.channels?.length ?? row?.channels.length ?? 0;
  const apiStatus = (detail?.status || row?.apiStatus || "").toLowerCase();
  const canModerate = apiStatus === "pending_approval";

  /** Refresh campaigns table + summary KPIs, then close the sheet. */
  const refreshTableAndClose = async (message: string) => {
    if (programId) {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: campaignKeys.list(programId, { pageSize: 100 }),
        }),
        queryClient.invalidateQueries({
          queryKey: campaignKeys.summary(programId),
        }),
        queryClient.invalidateQueries({
          queryKey: ["perkpoint", "campaigns", programId],
        }),
      ]);
    } else {
      await queryClient.invalidateQueries({ queryKey: campaignKeys.all });
    }
    toast.success(message);
    setRejectOpen(false);
    setRejectReason("");
    onOpenChange(false);
  };

  const approveMutation = useMutation({
    mutationFn: () => CampaignsApi.approve(programId!, campaignId!),
    onSuccess: async (approved) => {
      await refreshTableAndClose(
        `${approved.name || row?.name || "Campaign"} approved`,
      );
    },
    onError: (error) =>
      toast.error(apiErrorMessage(error, "Could not approve the campaign")),
  });

  const rejectMutation = useMutation({
    mutationFn: () =>
      CampaignsApi.reject(programId!, campaignId!, rejectReason),
    onSuccess: async (rejected) => {
      await refreshTableAndClose(
        `${rejected.name || row?.name || "Campaign"} rejected`,
      );
    },
    onError: (error) =>
      toast.error(apiErrorMessage(error, "Could not reject the campaign")),
  });

  const moderating = approveMutation.isPending || rejectMutation.isPending;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 overflow-hidden border-l border-slate-200 p-0 sm:max-w-2xl lg:max-w-3xl"
      >
        <div className="relative overflow-hidden border-b border-green-100 bg-gradient-to-br from-green-50 via-emerald-50/70 to-white px-5 pb-5 pt-6 sm:px-6">
          <div className="pointer-events-none absolute -right-8 -top-10 h-36 w-36 rounded-full bg-green-200/40 blur-2xl" />
          <div className="pointer-events-none absolute bottom-0 left-1/3 h-24 w-24 rounded-full bg-emerald-200/30 blur-2xl" />

          <div className="relative">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-green-600 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-white shadow-sm">
                <Megaphone className="h-3 w-3" />
                Campaign
              </span>
              {row ? <StatusPill status={row.status} /> : null}
              {row ? (
                <span className="inline-flex items-center rounded-full bg-white/80 px-2.5 py-1 text-xs font-medium text-slate-700 ring-1 ring-slate-200">
                  {row.type}
                </span>
              ) : null}
            </div>

            <SheetTitle className="pr-8 text-2xl font-bold tracking-tight text-slate-900">
              {row?.name || "Campaign details"}
            </SheetTitle>
            <SheetDescription className="mt-1.5 text-sm text-slate-600">
              {row ? (
                <span className="font-medium text-slate-700">{row.code}</span>
              ) : (
                "Loading campaign…"
              )}
              {row?.description ? (
                <span className="mt-1 block text-muted-foreground">
                  {row.description}
                </span>
              ) : null}
            </SheetDescription>

            {canModerate ? (
              <div className="mt-4 space-y-3">
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    className="bg-green-600 text-white hover:bg-green-700"
                    disabled={moderating}
                    onClick={() => {
                      setRejectOpen(false);
                      approveMutation.mutate();
                    }}
                  >
                    <Check className="mr-1.5 h-4 w-4" />
                    {approveMutation.isPending ? "Approving…" : "Approve"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="border-red-200 bg-white text-red-700 hover:bg-red-50 hover:text-red-800"
                    disabled={moderating}
                    onClick={() => setRejectOpen((open) => !open)}
                  >
                    <X className="mr-1.5 h-4 w-4" />
                    Reject
                  </Button>
                </div>
                {rejectOpen ? (
                  <div className="rounded-xl border border-red-100 bg-white/90 p-3 shadow-sm">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Rejection reason
                    </p>
                    <Textarea
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Optional note for the campaign author…"
                      className="min-h-[72px] resize-none bg-white"
                      disabled={moderating}
                    />
                    <div className="mt-2 flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={moderating}
                        onClick={() => {
                          setRejectOpen(false);
                          setRejectReason("");
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        className="bg-red-600 text-white hover:bg-red-700"
                        disabled={moderating}
                        onClick={() => rejectMutation.mutate()}
                      >
                        {rejectMutation.isPending
                          ? "Rejecting…"
                          : "Confirm reject"}
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {row ? (
              <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
                <StatTile
                  label="Reward"
                  value={row.bonusPoints}
                  icon={Coins}
                />
                <StatTile
                  label="Channels"
                  value={
                    channelCount
                      ? `${channelCount} channel${channelCount === 1 ? "" : "s"}`
                      : "None"
                  }
                  icon={MessageCircle}
                />
                <StatTile
                  label="Dates"
                  value={
                    row.endDate !== "—"
                      ? `${row.startDate} – ${row.endDate}`
                      : row.startDate
                  }
                  icon={CalendarClock}
                />
              </div>
            ) : null}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto bg-slate-50/40 px-5 py-4 sm:px-6">
          {!programId || !campaignId ? (
            <p className="text-sm text-muted-foreground">
              Missing program or campaign id.
            </p>
          ) : detailQuery.isLoading ? (
            <div className="space-y-3">
              <div className="h-10 animate-pulse rounded-xl bg-slate-200/70" />
              <div className="h-40 animate-pulse rounded-xl bg-slate-200/50" />
              <div className="h-28 animate-pulse rounded-xl bg-slate-200/40" />
            </div>
          ) : detailQuery.isError ? (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {apiErrorMessage(detailQuery.error, "Could not load campaign")}
            </div>
          ) : detail ? (
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="mb-4 flex h-auto w-full flex-wrap justify-start gap-1 rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
                {(
                  [
                    ["overview", "Overview"],
                    ["audience", "Audience"],
                    ["reward", "Reward"],
                    ["channels", "Channels"],
                    ["schedule", "Schedule"],
                  ] as const
                ).map(([value, label]) => (
                  <TabsTrigger
                    key={value}
                    value={value}
                    className="rounded-lg px-3 py-1.5 text-sm data-[state=active]:bg-green-600 data-[state=active]:text-white data-[state=active]:shadow-sm"
                  >
                    {label}
                  </TabsTrigger>
                ))}
              </TabsList>
              <TabsContent value="overview" className="mt-0">
                <OverviewTab detail={detail} row={row!} />
              </TabsContent>
              <TabsContent value="audience" className="mt-0">
                <AudienceTab detail={detail} />
              </TabsContent>
              <TabsContent value="reward" className="mt-0">
                <RewardTab detail={detail} />
              </TabsContent>
              <TabsContent value="channels" className="mt-0">
                <ChannelsTab detail={detail} />
              </TabsContent>
              <TabsContent value="schedule" className="mt-0">
                <ScheduleTab detail={detail} />
              </TabsContent>
            </Tabs>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
